import { Container, Title, Text, Alert, Loader, Center, Stack, Modal } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { ChatLayout } from '../../components/layout/ChatLayout'
import { useAgents, agentUtils } from '../../api'
import { useAgentsSearch, useAgentsModal } from '../../stores/agentsStore'
import { useChatStore } from '../../stores/chatStore'
import styles from './AgentsPage.module.css'
import { SearchBar } from '../../components/common/SearchBar'
import { trackComponentInteraction } from '../../utils/analytics';
import { AgentCard, AgentDetailModal } from '../../features/agents/components'
import { LoadingState } from '../../components/common/LoadingState'

export function AgentsPage() {
  // Use existing API hooks (already implemented)
  const { data: agents, isLoading, isError, error } = useAgents()
  
  // Use existing store (already implemented)
  const { query: searchQuery, setQuery, clearQuery } = useAgentsSearch()
  const { open: openDetail } = useAgentsModal()
  const { isOpen, close } = useAgentsModal()
  const hasGenieAccess = useChatStore(state => state.hasGenieAccess)

  // Wrap setSearchQuery to add analytics tracking
  const setSearchQuery = (query: string) => {
    if (query.trim() && query.trim() !== searchQuery.trim()) {
      trackComponentInteraction(
        'Agent Header',
        'Agent Search',
        'input',
        'submit',
        'agent_search_performed'
      );
    }
    setQuery(query);
  }

  const trimmedQuery = searchQuery.trim()
  // Filter out Genie agent if user doesn't have access
  const visibleAgentsBase = (agents ?? []).filter(agent => {
    if (agent.id === 'genie') {
      return hasGenieAccess;
    }
    return true;
  });

  const filteredAgents = agentUtils.searchAgents(visibleAgentsBase, searchQuery)
  const pinnedAgents = agentUtils.getPinnedAgents(visibleAgentsBase)
  const recommendedAgents = agentUtils.getRecommendedAgents(visibleAgentsBase)

  // Initial loading state — grid skeleton covering full page including search
  if (isLoading) {
    return (
      <ChatLayout>
        <LoadingState
          message="Loading agents..."
          showSkeleton
          skeletonType="agents"
          fullHeight
        />
      </ChatLayout>
    )
  }

  // Error state
  if (isError) {
    return (
      <ChatLayout>
        <Container size="md" py="xl">
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error loading agents"
            color="red"
            variant="light"
          >
            {error?.message || 'Failed to load agents'}
          </Alert>
        </Container>
      </ChatLayout>
    )
  }

  // Success state
  return (
    <ChatLayout>
      <Container size="xl" className={styles.container}>
        {/* Agent Detail Modal Wrapper */}
        <Modal
          opened={isOpen}
          onClose={close}
          withCloseButton={false}
          centered
          radius={6}
          padding={0}
          size={606}
          overlayProps={{ opacity: 0.2 }}
        >
          <AgentDetailModal />
        </Modal>

        {/* Main Agents Title */}
        <Title order={1} className={styles.title}>
          Agents
        </Title>
        
        {/* Content Stack */}
        <Stack gap={42} align="center" className={styles.contentStack}>
          {/* Search */}
          <div className={styles.searchWrapper}>
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={clearQuery}
              placeholder="Search"
              ariaLabel="Search agents"
            />
          </div>

          {trimmedQuery.length > 0 ? (
            // Results section when searching
            <Stack gap={18} align="center" className={styles.section}>
              <Text className={styles.sectionTitle}>Results</Text>
              <div className={styles.agentGrid}>
                {filteredAgents.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" className={styles.emptyState}>
                    No agents match your search
                  </Text>
                ) : (
                  filteredAgents.map(agent => (
                    <div key={agent.id} className={styles.agentGridItem}>
                      <AgentCard
                        agent={agent}
                        variant="small"
                        onClick={openDetail}
                      />
                    </div>
                  ))
                )}
              </div>
            </Stack>
          ) : (
            <>
              {/* Pinned Section */}
              <Stack gap={18} align="center" className={styles.section}>
                <Text className={styles.sectionTitle}>Pinned</Text>
                <div className={styles.agentGrid}>
                  {pinnedAgents.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center" className={styles.emptyState}>
                      No pinned agents yet
                    </Text>
                  ) : (
                    pinnedAgents.map(agent => (
                      <div key={agent.id} className={styles.agentGridItem}>
                        <AgentCard
                          agent={agent}
                          variant="large"
                          onClick={openDetail}
                        />
                      </div>
                    ))
                  )}
                </div>
              </Stack>

              {/* Recommended Section */}
              <Stack gap={18} align="center" className={styles.section}>
                <Text className={styles.sectionTitle}>Recommended</Text>
                <div className={styles.agentGrid}>
                  {recommendedAgents.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center" className={styles.emptyState}>
                      No recommended agents available
                    </Text>
                  ) : (
                    recommendedAgents.map(agent => (
                      <div key={agent.id} className={styles.agentGridItem}>
                        <AgentCard
                          agent={agent}
                          variant="large"
                          onClick={openDetail}
                        />
                      </div>
                    ))
                  )}
                </div>
              </Stack>
            </>
          )}
        </Stack>
      </Container>
    </ChatLayout>
  )
}
